import { Typography } from "@outschool/backpack";
import {
  CreditAccountStatus,
  PricingOfferState,
} from "@outschool/gql-backend-generated";
import { TFunction, Trans, useTranslation } from "@outschool/localization";
import { Banner } from "@outschool/ui-legacy-component-library";
import React from "react";

import {
  ActivityEnrollQueryQuery,
  BulkPricingBreakdownQuery,
  CurrentUserQueryQuery,
  PricingBreakdownQueryQuery,
} from "../../../../shared/generated/graphql";
import { getEmailToConfirmToUseCredit } from "../../../../shared/User";
import { useResendEmailConfirmation } from "../../../hooks/CurrentUser";
import { getPricingOfferEnrollmentCopy } from "../../../hooks/PricingOffers";
import { useCreditAccountCached } from "../../../queries/CreditQueries";
import EmailBanner from "../../banners/EmailBanner";

type WarningMessagesProps = {
  enrollingParent?:
    | ActivityEnrollQueryQuery["currentUser"]
    | CurrentUserQueryQuery["currentUser"];
  pricingOfferState: PricingOfferState | undefined;
  pricingOffer:
    | PricingBreakdownQueryQuery["pricingBreakdown"]["pricingOffer"]
    | BulkPricingBreakdownQuery["bulkPricingBreakdown"]["pricingOffer"]
    | null
    | undefined;
};
export function WarningMessages({
  enrollingParent,
  pricingOffer,
  pricingOfferState,
}: WarningMessagesProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\enrollmentForm\\WarningMessages",
  );
  const { creditAccount } = useCreditAccountCached();
  const emailToConfirm = getEmailToConfirmToUseCredit(
    enrollingParent,
    pricingOffer,
  );
  const resendEmailConfirmation = useResendEmailConfirmation(
    enrollingParent!,
    emailToConfirm,
  );

  const showEmailConfirmationCreditBanner =
    (creditAccount?.openToBuy ?? 0) > 0 &&
    creditAccount?.status === CreditAccountStatus.RequiresConfirmedPrimaryEmail;
  if (showEmailConfirmationCreditBanner) {
    return <CreditEmailWarning {...{ t, resendEmailConfirmation }} />;
  }

  if (pricingOfferState === PricingOfferState.InvalidUnconfirmedEmail) {
    return (
      <PricingOfferEmailWarning
        {...{ t, resendEmailConfirmation, pricingOffer }}
      />
    );
  }

  if (
    creditAccount?.status === CreditAccountStatus.InvalidSharedPaymentMethod
  ) {
    return <CreditSharedPaymentMethodWarning t={t} />;
  }

  return null;
}

interface CreditEmailWarningProps {
  resendEmailConfirmation: () => Promise<boolean | undefined>;
  t: TFunction;
}

function CreditEmailWarning({
  resendEmailConfirmation,
  t,
}: CreditEmailWarningProps) {
  return (
    <EmailBanner showResendButton onResendButtonClick={resendEmailConfirmation}>
      <Trans t={t}>
        You have Outschool Credits you could use for this purchase.{" "}
        <Typography
          variant="inherit"
          sx={{
            fontWeight: "fontWeightBold",
          }}
        >
          Please confirm your email to use your available Outschool Credits.
        </Typography>
      </Trans>
    </EmailBanner>
  );
}

interface PricingOfferEmailWarningProps {
  resendEmailConfirmation: () => Promise<boolean | undefined>;
  t: TFunction;
  pricingOffer: WarningMessagesProps["pricingOffer"];
}

function PricingOfferEmailWarning({
  resendEmailConfirmation,
  t,
  pricingOffer,
}: PricingOfferEmailWarningProps) {
  const pricingOfferCopy = getPricingOfferEnrollmentCopy(pricingOffer);
  return (
    <EmailBanner showResendButton onResendButtonClick={resendEmailConfirmation}>
      <Trans t={t}>
        This purchase may be eligible for {{ pricingOfferCopy }} offer.
        <Typography
          variant="inherit"
          sx={{
            fontWeight: "fontWeightBold",
          }}
        >
          Please confirm your email to use your organization&apos;s funds.
        </Typography>
      </Trans>
    </EmailBanner>
  );
}

function CreditSharedPaymentMethodWarning({ t }: { t: TFunction }) {
  return (
    <Banner data-test-id="shared-payment-method-warning" variant={"warning"}>
      <Trans t={t}>
        This credit (promo code) cannot be used at this time.{" "}
        <Typography variant="inherit" sx={{ fontWeight: "fontWeightBold" }}>
          Please contact support@outschool.com if you believe this to be an
          error.
        </Typography>
      </Trans>
    </Banner>
  );
}
